// First Created - 08 Oct 2026
// Author - Atul Pandey
// Purpose - API slice for Org Trail Session Content Assignments


import {
    createSelector,
    createEntityAdapter,
} from '@reduxjs/toolkit'


import {
    apiSlice,
} from '@/app/api/apiSlice'



/*
|--------------------------------------------------------------------------
| 01 - Entity Adapter
|--------------------------------------------------------------------------
*/

const orgTrailSessionContentAssignmentsAdapter =
    createEntityAdapter({})


const initialState =
    orgTrailSessionContentAssignmentsAdapter
        .getInitialState()



/*
|--------------------------------------------------------------------------
| 02 - API Slice
|--------------------------------------------------------------------------
*/

export const orgTrailSessionContentAssignmentsApiSlice =
    apiSlice.injectEndpoints({

        endpoints:
            builder => ({


                /*
                |--------------------------------------------------------------------------
                | 01 - Get Content Assignments
                |--------------------------------------------------------------------------
                */

                getOrgTrailSessionContentAssignments:
                    builder.query({

                        query: ({
                            orgId = '',
                            trailId = '',
                            sessionId = '',
                            cohortId = '',
                            referenceContentId = '',
                            active = '',
                        } = {}) => ({

                            url:
                                '/orgTrailSessionContentAssignments/getOrgTrailSessionContentAssignments',

                            params: {

                                ...(orgId
                                    ? {
                                        orgId
                                    }
                                    : {}),


                                ...(trailId
                                    ? {
                                        trailId
                                    }
                                    : {}),


                                ...(sessionId
                                    ? {
                                        sessionId
                                    }
                                    : {}),


                                ...(cohortId
                                    ? {
                                        cohortId
                                    }
                                    : {}),


                                ...(referenceContentId
                                    ? {
                                        referenceContentId
                                    }
                                    : {}),


                                ...(active !== ''
                                    ? {
                                        active
                                    }
                                    : {}),

                            },


                            validateStatus: (
                                response,
                                result
                            ) => {

                                return (
                                    response.status ===
                                        200 &&
                                    !result?.isError
                                )

                            },

                        }),


                        /*
                        |--------------------------------------------------------------------------
                        | Normalize Backend Array
                        |--------------------------------------------------------------------------
                        */

                        transformResponse:
                            responseData => {

                                const assignments =
                                    Array.isArray(
                                        responseData
                                    )
                                        ?
                                        responseData
                                        :
                                        []


                                const loadedAssignments =
                                    assignments.map(
                                        assignment => ({

                                            ...assignment,

                                            id:
                                                assignment._id,

                                        })
                                    )


                                return orgTrailSessionContentAssignmentsAdapter
                                    .setAll(
                                        initialState,
                                        loadedAssignments
                                    )

                            },


                        providesTags: (
                            result
                        ) => {

                            if (
                                result?.ids
                            ) {

                                return [

                                    {
                                        type:
                                            'OrgTrailSessionContentAssignment',

                                        id:
                                            'LIST',
                                    },


                                    ...result.ids.map(
                                        id => ({

                                            type:
                                                'OrgTrailSessionContentAssignment',

                                            id,

                                        })
                                    ),

                                ]

                            }


                            return [

                                {
                                    type:
                                        'OrgTrailSessionContentAssignment',

                                    id:
                                        'LIST',
                                },

                            ]

                        },

                    }),



                /*
                |--------------------------------------------------------------------------
                | 02 - Get Content Assignment By ID
                |--------------------------------------------------------------------------
                */

                getOrgTrailSessionContentAssignmentById:
                    builder.query({

                        query: (id) => ({

                            url:
                                `/orgTrailSessionContentAssignments/getOrgTrailSessionContentAssignmentById/${id}`,

                            validateStatus: (
                                response,
                                result
                            ) => {

                                return (
                                    response.status ===
                                        200 &&
                                    !result?.isError
                                )

                            },

                        }),


                        transformResponse:
                            responseData => {

                                if (
                                    !responseData
                                ) {
                                    return null
                                }

                                return {

                                    ...responseData,

                                    id:
                                        responseData._id,

                                }

                            },


                        providesTags: (
                            result,
                            error,
                            id
                        ) => [

                            {
                                type:
                                    'OrgTrailSessionContentAssignment',

                                id,
                            },

                        ],

                    }),



                /*
                |--------------------------------------------------------------------------
                | 03 - Create Content Assignment
                |--------------------------------------------------------------------------
                */

                createOrgTrailSessionContentAssignment:
                    builder.mutation({

                        query:
                            initialAssignment => ({

                                url:
                                    '/orgTrailSessionContentAssignments/createOrgTrailSessionContentAssignment',

                                method:
                                    'POST',

                                body: {
                                    ...initialAssignment,
                                },

                            }),


                        invalidatesTags: [

                            {
                                type:
                                    'OrgTrailSessionContentAssignment',

                                id:
                                    'LIST',
                            },

                            {
                                type:
                                    'MyAssignedSessionReferenceContent',

                                id:
                                    'LIST',
                            },

                        ],

                    }),



                /*
                |--------------------------------------------------------------------------
                | 04 - Update Content Assignment
                |--------------------------------------------------------------------------
                */

                updateOrgTrailSessionContentAssignment:
                    builder.mutation({

                        query:
                            assignment => ({

                                url:
                                    `/orgTrailSessionContentAssignments/updateOrgTrailSessionContentAssignment/${assignment.id || assignment._id}`,

                                method:
                                    'PATCH',

                                body: {
                                    active:
                                        assignment.active,
                                },

                            }),


                        invalidatesTags: (
                            result,
                            error,
                            arg
                        ) => [

                            {
                                type:
                                    'OrgTrailSessionContentAssignment',

                                id:
                                    arg.id || arg._id,
                            },

                            {
                                type:
                                    'OrgTrailSessionContentAssignment',

                                id:
                                    'LIST',
                            },

                            {
                                type:
                                    'MyAssignedSessionReferenceContent',

                                id:
                                    'LIST',
                            },

                        ],

                    }),



                /*
                |--------------------------------------------------------------------------
                | 05 - Delete Content Assignment
                |--------------------------------------------------------------------------
                */

                deleteOrgTrailSessionContentAssignment:
                    builder.mutation({

                        query: ({
                            id,
                        }) => ({

                            url:
                                `/orgTrailSessionContentAssignments/deleteOrgTrailSessionContentAssignment/${id}`,

                            method:
                                'DELETE',

                        }),


                        invalidatesTags: (
                            result,
                            error,
                            arg
                        ) => [

                            {
                                type:
                                    'OrgTrailSessionContentAssignment',

                                id:
                                    arg.id,
                            },

                            {
                                type:
                                    'OrgTrailSessionContentAssignment',

                                id:
                                    'LIST',
                            },

                            {
                                type:
                                    'MyAssignedSessionReferenceContent',

                                id:
                                    'LIST',
                            },

                        ],

                    }),



                /*
                |--------------------------------------------------------------------------
                | 06 - Get My Assigned Session Reference Content
                |--------------------------------------------------------------------------
                |
                | User-facing endpoint.
                |
                | The backend resolves actual cohort eligibility.
                |
                | The frontend does not pass userId or cohortId.
                |
                |--------------------------------------------------------------------------
                */

                getMyAssignedSessionReferenceContent:
                    builder.query({

                        query: ({
                            orgId,
                            trailId,
                            sessionId,
                        }) => ({

                            url:
                                '/orgTrailSessionContentAssignments/getMyAssignedSessionReferenceContent',

                            params: {

                                orgId,

                                trailId,

                                sessionId,

                            },


                            validateStatus: (
                                response,
                                result
                            ) => {

                                return (
                                    response.status ===
                                        200 &&
                                    !result?.isError
                                )

                            },

                        }),


                        /*
                        |--------------------------------------------------------------------------
                        | Normalize Visible Reference Content
                        |--------------------------------------------------------------------------
                        */

                        transformResponse:
                            responseData => {

                                const content =
                                    Array.isArray(
                                        responseData
                                    )
                                        ?
                                        responseData
                                        :
                                        []


                                const loadedContent =
                                    content.map(
                                        item => ({

                                            ...item,

                                            id:
                                                item._id,

                                        })
                                    )


                                return sessionVisibleContentAdapter
                                    .setAll(
                                        sessionVisibleContentInitialState,
                                        loadedContent
                                    )

                            },


                        providesTags: [

                            {
                                type:
                                    'MyAssignedSessionReferenceContent',

                                id:
                                    'LIST',
                            },

                        ],

                    }),

            }),

    })



/*
|--------------------------------------------------------------------------
| 03 - Adapter For User Visible Content
|--------------------------------------------------------------------------
*/

const sessionVisibleContentAdapter =
    createEntityAdapter({})


const sessionVisibleContentInitialState =
    sessionVisibleContentAdapter
        .getInitialState()



/*
|--------------------------------------------------------------------------
| 04 - Hooks
|--------------------------------------------------------------------------
*/

export const {

    useGetOrgTrailSessionContentAssignmentsQuery,

    useLazyGetOrgTrailSessionContentAssignmentsQuery,

    useGetOrgTrailSessionContentAssignmentByIdQuery,

    useLazyGetOrgTrailSessionContentAssignmentByIdQuery,

    useCreateOrgTrailSessionContentAssignmentMutation,

    useUpdateOrgTrailSessionContentAssignmentMutation,

    useDeleteOrgTrailSessionContentAssignmentMutation,

    useGetMyAssignedSessionReferenceContentQuery,

    useLazyGetMyAssignedSessionReferenceContentQuery,

} =
    orgTrailSessionContentAssignmentsApiSlice



/*
|--------------------------------------------------------------------------
| 05 - Base Selector
|--------------------------------------------------------------------------
*/

export const selectOrgTrailSessionContentAssignmentsResult =

    orgTrailSessionContentAssignmentsApiSlice
        .endpoints
        .getOrgTrailSessionContentAssignments
        .select()



/*
|--------------------------------------------------------------------------
| 06 - Assignment Data Selector
|--------------------------------------------------------------------------
*/

const selectOrgTrailSessionContentAssignmentsData =

    createSelector(

        selectOrgTrailSessionContentAssignmentsResult,

        assignmentResult =>
            assignmentResult.data

    )



/*
|--------------------------------------------------------------------------
| 07 - Entity Selectors
|--------------------------------------------------------------------------
*/

export const {

    selectAll:
        selectAllOrgTrailSessionContentAssignments,

    selectById:
        selectOrgTrailSessionContentAssignmentById,

    selectIds:
        selectOrgTrailSessionContentAssignmentIds,

} =

    orgTrailSessionContentAssignmentsAdapter
        .getSelectors(

            state =>

                selectOrgTrailSessionContentAssignmentsData(
                    state
                ) ??

                initialState

        )